import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CompanyProfile.css";

import mainImage from "../../../../assets/images/home/companyprofile/main.jpg";
import downloadIcon from "../../../../assets/icons/download.svg";

import p1 from "../../../../assets/images/home/companyprofile/p1.png";
import p2 from "../../../../assets/images/home/companyprofile/p2.png";
import p3 from "../../../../assets/images/home/companyprofile/p3.png";
import p4 from "../../../../assets/images/home/companyprofile/p4.png";
import { getCompanyProfilePdfs } from "../../../../api/companyProfile";
import { resolveMediaUrl } from "../../../../utils/media";

const defaultPartnerFallbacks = [p1, p2, p3, p4];

const fallbackCompanyProfile = {
  tag: "COMPANY PROFILE",
  titleLine1: "Get to know our profile and",
  titleLine2: "learn more about IRQGYP",
  description:
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions — headquartered in Baghdad.",
  button1Text: "Download company profile",
  button1Link: "/downloads",
  button2Text: "IRQGYP Technical Manual",
  button2Link: "/downloads",
  button3Text: "Certifications",
  button3Link: "/downloads",
  bottomText: "Integrated Systems for Every Construction Need",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceCompanyPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceCompanyPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeProfileWrapper = (node, into) => {
    const wrap = node?.companyProfile ?? node?.CompanyProfile;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeProfileWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeProfileWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeProfileWrapper(base, base);
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const getMainImageCandidates = (c) => {
  if (!c || typeof c !== "object") return [];
  return [
    c.mainImage,
    c.heroImage,
    c.image,
    c.bannerImage,
    c.mediaUrl,
    c.MediaUrl,
    c.photo,
  ].filter((x) => x != null && x !== "");
};

const resolvePartnerSlot = (payload, index) => {
  const i = String(index);
  const candidates = [
    payload?.[`partnerLogo${i}`],
    payload?.[`PartnerLogo${i}`],
    payload?.[`partner_logo_${i}`],
    payload?.[`item${i}Image`],
    payload?.[`item${i}image`],
    payload?.[`item${i}MediaUrl`],
    payload?.[`item${i}mediaUrl`],
    payload?.[`item${i}Logo`],
    payload?.[`item${i}logo`],
    payload?.[`item${i}Media`],
    payload?.[`item${i}media`],
    payload?.[`logo${i}`],
    payload?.[`partner${i}`],
    payload?.[`partner${i}Image`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const collectPartnerLogos = (payload) => {
  const out = [];
  for (let n = 1; n <= 12; n++) {
    const url = resolvePartnerSlot(payload, n);
    if (url) out.push(url);
  }
  return out;
};

const unwrapCompanyProfilePdfs = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.data)) return data.data;
  return [];
};

const pickFirstCompanyProfilePdf = (data) => {
  for (const row of unwrapCompanyProfilePdfs(data)) {
    const resolvedUrl = resolveMediaUrl(row?.fileUrl);
    if (resolvedUrl) {
      return { resolvedUrl, title: row?.title || "Company profile" };
    }
  }
  return null;
};

const sanitizeDownloadFileName = (value) =>
  String(value || "document")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const triggerBlobDownload = (blob, fileName) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

const hasMeaningfulCompanyPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag)) return true;
  if (pickString(c.titleLine1, c.titleLine2, c.title, c.heading)) return true;
  if (pickString(c.description, c.body, c.text, c.summary)) return true;
  if (
    pickString(
      c.button1Text,
      c.button2Text,
      c.button3Text,
      c.button1Link,
      c.button2Link,
      c.button3Link,
      c.primaryButtonText,
      c.primaryButtonLink,
      c.secondaryButton1Text,
      c.secondaryButton1Link,
      c.secondaryButton2Text,
      c.secondaryButton2Link
    )
  )
    return true;
  if (pickString(c.bottomLine1, c.bottomLine2, c.bottomText, c.footerTagline)) return true;
  if (collectPartnerLogos(c).length) return true;
  for (const candidate of getMainImageCandidates(c)) {
    if (resolveMediaUrl(candidate)) return true;
  }
  return false;
};

const CompanyProfile = ({ content }) => {
  const navigate = useNavigate();
  const [primaryCompanyPdf, setPrimaryCompanyPdf] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getCompanyProfilePdfs();
        if (!isMounted) return;
        setPrimaryCompanyPdf(pickFirstCompanyProfilePdf(data));
      } catch {
        if (isMounted) setPrimaryCompanyPdf(null);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const view = useMemo(() => {
    const payload = coalesceCompanyPayload(content);
    if (!hasMeaningfulCompanyPayload(payload) || !payload) {
      return {
        tag: fallbackCompanyProfile.tag,
        titleLine1: fallbackCompanyProfile.titleLine1,
        titleLine2: fallbackCompanyProfile.titleLine2,
        description: fallbackCompanyProfile.description,
        buttons: [
          { text: fallbackCompanyProfile.button1Text, link: fallbackCompanyProfile.button1Link },
          { text: fallbackCompanyProfile.button2Text, link: fallbackCompanyProfile.button2Link },
          { text: fallbackCompanyProfile.button3Text, link: fallbackCompanyProfile.button3Link },
        ],
        bottomText: fallbackCompanyProfile.bottomText,
        mainSrc: mainImage,
        partners: defaultPartnerFallbacks.map((fb, idx) => ({ src: fb, fallback: fb })),
      };
    }

    let finalLine1 = pickString(payload.titleLine1, payload.heading);
    let finalLine2 = pickString(payload.titleLine2, payload.subheading);
    const singleTitle = pickString(payload.title);
    if (!finalLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        finalLine1 = parts[0] || "";
        finalLine2 = parts.slice(1).join(" ").trim() || finalLine2;
      } else {
        finalLine1 = singleTitle;
      }
    }
    if (!finalLine1) finalLine1 = fallbackCompanyProfile.titleLine1;
    if (!finalLine2) {
      if (!(singleTitle && !singleTitle.includes("\n"))) {
        finalLine2 = fallbackCompanyProfile.titleLine2;
      }
    }

    const legacyBottom =
      pickString(payload.bottomLine1) && pickString(payload.bottomLine2)
        ? `${pickString(payload.bottomLine1)} ${pickString(payload.bottomLine2)}`.trim()
        : pickString(payload.bottomLine1, payload.bottomLine2);
    const bottomText =
      pickString(payload.bottomText, payload.footerTagline, legacyBottom) ||
      fallbackCompanyProfile.bottomText;

    const apiLogos = collectPartnerLogos(payload);
    const partners =
      apiLogos.length > 0
        ? apiLogos.map((src, idx) => ({ src, fallback: defaultPartnerFallbacks[idx % defaultPartnerFallbacks.length] }))
        : defaultPartnerFallbacks.map((fb) => ({ src: fb, fallback: fb }));

    let mainSrc = mainImage;
    for (const c of getMainImageCandidates(payload)) {
      const r = resolveMediaUrl(c);
      if (r) {
        mainSrc = r;
        break;
      }
    }

    return {
      tag: pickString(payload.tag, payload.sectionTag) || fallbackCompanyProfile.tag,
      titleLine1: finalLine1,
      titleLine2: finalLine2,
      description:
        pickString(payload.description, payload.body, payload.text, payload.summary) || fallbackCompanyProfile.description,
      buttons: [
        {
          text:
            pickString(payload.button1Text, payload.primaryButtonText, payload.downloadButtonText) ||
            fallbackCompanyProfile.button1Text,
          link: pickString(payload.button1Link, payload.primaryButtonLink) || fallbackCompanyProfile.button1Link,
        },
        {
          text:
            pickString(
              payload.button2Text,
              payload.secondaryButtonText,
              payload.secondaryButton1Text
            ) || fallbackCompanyProfile.button2Text,
          link:
            pickString(payload.button2Link, payload.secondaryButtonLink, payload.secondaryButton1Link) ||
            fallbackCompanyProfile.button2Link,
        },
        {
          text:
            pickString(payload.button3Text, payload.tertiaryButtonText, payload.secondaryButton2Text) ||
            fallbackCompanyProfile.button3Text,
          link:
            pickString(payload.button3Link, payload.tertiaryButtonLink, payload.secondaryButton2Link) ||
            fallbackCompanyProfile.button3Link,
        },
      ],
      bottomText,
      mainSrc,
      partners,
    };
  }, [content]);

  const handleCompanyButtonClick = useCallback(
    async (btn, idx) => {
      if (idx === 0 && primaryCompanyPdf?.resolvedUrl) {
        try {
          const res = await fetch(primaryCompanyPdf.resolvedUrl);
          if (!res.ok) throw new Error("fetch failed");
          const blob = await res.blob();
          const rawName = sanitizeDownloadFileName(primaryCompanyPdf.title);
          const fileName = /\.pdf$/i.test(rawName) ? rawName : `${rawName}.pdf`;
          triggerBlobDownload(blob, fileName);
        } catch {
          window.open(primaryCompanyPdf.resolvedUrl, "_blank", "noopener,noreferrer");
        }
        return;
      }
      navigate(btn.link || "/downloads");
    },
    [navigate, primaryCompanyPdf]
  );

  return (
    <section className="company">
      <div className="company-container">
        <span className="company-tag">{view.tag}</span>

        <h2 className="company-title">
          {view.titleLine1}
          {view.titleLine2 ? (
            <>
              <br />
              <span>{view.titleLine2}</span>
            </>
          ) : null}
        </h2>

        <p className="company-desc">{view.description}</p>

        <div className="company-buttons">
          {view.buttons.map((btn, idx) => {
            const isPrimary = idx === 0;
            return (
              <button
                key={`${btn.text}-${idx}`}
                className={isPrimary ? "buttongyp" : "btn-outlinecp"}
                type="button"
                onClick={() => handleCompanyButtonClick(btn, idx)}
              >
                {btn.text}
                <img src={downloadIcon} alt="" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="company-image">
        <img
          src={view.mainSrc}
          alt=""
          onError={(e) => {
            if (e.currentTarget.src !== mainImage) {
              e.currentTarget.src = mainImage;
            }
          }}
        />
      </div>

      <div className="company-bottom">
        <p>{view.bottomText}</p>

        <div className="company-logos">
          {view.partners.map((p, i) => (
            <img
              src={p.src}
              key={`${p.src}-${i}`}
              alt=""
              onError={(e) => {
                if (e.currentTarget.src !== p.fallback) {
                  e.currentTarget.src = p.fallback;
                }
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompanyProfile;
